import React, { useState } from "react";
import { X, Link2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

/**
 * Site create / edit dialog. When `site` is provided, edits it via PATCH;
 * otherwise creates a fresh row via POST /projects. Supports the SolarEdge
 * "paste anything" import — a URL, "siteId, apiKey" pair, or explicit fields.
 */
export default function AddSiteDialog({ site, onClose, onSaved }) {
  const isEdit = !!site;
  const [tab, setTab] = useState("basics"); // basics | solaredge | meteronline | geo | rates
  const [name, setName] = useState(site?.name || "");
  const [customer, setCustomer] = useState(site?.customer_name || "");
  const [address, setAddress] = useState(site?.address || "");
  const [postcode, setPostcode] = useState(site?.postcode || "");
  const [dataSource, setDataSource] = useState(site?.data_source || "solaredge");
  const [siteId, setSiteId] = useState(site?.solaredge_site_id || "");
  const [apiKey, setApiKey] = useState(site?.solaredge_api_key || "");
  const [apiString, setApiString] = useState("");
  // Solis (SolisCloud) — HMAC KeyID + KeySecret + inverter ID
  const [solisKeyId, setSolisKeyId] = useState(site?.solis_key_id || "");
  const [solisKeySecret, setSolisKeySecret] = useState(site?.solis_key_secret || "");
  const [solisInverterId, setSolisInverterId] = useState(site?.solis_inverter_id || "");
  const [solisApiBase, setSolisApiBase] = useState(site?.solis_api_base || "");
  const [solisTest, setSolisTest] = useState(null);
  // MeterOnline — modern (Basic Auth) + legacy (Bearer key) both supported
  const [moUser, setMoUser] = useState(site?.meteronline_username || "");
  const [moPass, setMoPass] = useState(site?.meteronline_password || "");
  const [importMeter, setImportMeter] = useState(
    site?.import_meter_number || site?.import_mpan || "");
  const [exportMeter, setExportMeter] = useState(
    site?.export_meter_number || site?.export_mpan || "");
  const [genMeter, setGenMeter] = useState(
    site?.generation_meter_number || site?.generation_mpan || "");
  // Legacy fields (kept editable for backwards compat with old integrations)
  const [moUrl, setMoUrl] = useState(site?.meteronline_api_url || "");
  const [moKey, setMoKey] = useState(site?.meteronline_api_key || "");
  const [showLegacy, setShowLegacy] = useState(
    !!(site?.meteronline_api_url || site?.meteronline_api_key));
  const [moTest, setMoTest] = useState(null);
  const [latitude, setLatitude] = useState(site?.latitude ?? "");
  const [longitude, setLongitude] = useState(site?.longitude ?? "");
  const [pr, setPr] = useState(site?.performance_ratio ?? 0.80);
  // Site-level P&L rate overrides — empty string means "inherit"
  const [ppaRate, setPpaRate] = useState(site?.pnl_ppa_p_per_kwh ?? "");
  const [exportRate, setExportRate] = useState(site?.pnl_export_p_per_kwh ?? "");
  const [gridRate, setGridRate] = useState(site?.pnl_grid_p_per_kwh ?? "");
  const [adminRate, setAdminRate] = useState(site?.pnl_admin_p_per_kwh ?? "");
  const [opexKwp, setOpexKwp] = useState(site?.opex_per_kwp_gbp ?? "");
  const [busy, setBusy] = useState(false);

  const testMeterOnline = async (meter, reverse = false) => {
    if (!meter || !meter.trim()) {
      toast.error("Enter the meter number first");
      return;
    }
    const useModern = moUser.trim() && moPass.trim();
    const useLegacy = moUrl.trim() && moKey.trim();
    if (!useModern && !useLegacy) {
      toast.error("Enter username + API password (or legacy URL + key) first");
      return;
    }
    setMoTest({ loading: true });
    try {
      const body = useModern
        ? { username: moUser, password: moPass, meter_number: meter, reverse }
        : { api_url: moUrl, api_key: moKey, meter_number: meter };
      const { data } = await api.post("/analytics/meteronline/test", body);
      setMoTest(data);
      if (data.ok) toast.success(`Got ${data.sample_count} readings · ${data.total_kwh_24h} kWh in 24h`);
      else toast.error(`MeterOnline: ${data.error}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setMoTest(null); }
  };

  const testSolis = async () => {
    if (!solisKeyId.trim() || !solisKeySecret.trim()) {
      toast.error("Enter Solis Key ID + Key Secret first");
      return;
    }
    setSolisTest({ loading: true });
    try {
      const { data } = await api.post("/analytics/solis/test", {
        key_id: solisKeyId, key_secret: solisKeySecret,
        api_base: solisApiBase || null,
      });
      setSolisTest(data);
      if (data.ok) toast.success(`Solis OK · ${data.station_count} station(s) visible`);
      else toast.error(`Solis: ${data.error}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSolisTest(null); }
  };

  const parseString = async () => {
    if (!apiString.trim()) return;
    try {
      const { data } = await api.post("/analytics/solaredge/parse", { api_string: apiString });
      if (data.site_id) setSiteId(data.site_id);
      if (data.api_key) setApiKey(data.api_key);
      if (data.site_id || data.api_key) {
        toast.success(`Parsed ${[data.site_id && "site ID", data.api_key && "API key"].filter(Boolean).join(" + ")}`);
      } else {
        toast.error("Couldn't parse — paste site ID + API key manually below.");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!name.trim() || !customer.trim() || !address.trim()) {
      toast.error("Name, customer, address are required");
      return;
    }
    setBusy(true);
    const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    // Auto-flip data_source to 'meteronline' if the user filled the MO
    // credentials but left Basics on the default 'solaredge'. Saves
    // operators from the "why isn't my MO data syncing?" support ticket
    // caused by mismatched Basics vs MeterOnline tabs.
    const moConfigured =
      (moUser.trim() && moPass.trim() && importMeter.trim() && exportMeter.trim()) ||
      (moUrl.trim() && moKey.trim() && importMeter.trim() && exportMeter.trim());
    const effectiveDataSource =
      dataSource === "solaredge" && moConfigured && !siteId.trim()
        ? "meteronline" : dataSource;
    const body = {
      name, customer_name: customer, address, postcode,
      data_source: effectiveDataSource,
      solaredge_site_id: siteId || null,
      solaredge_api_key: apiKey || null,
      solis_key_id: solisKeyId || null,
      solis_key_secret: solisKeySecret || null,
      solis_inverter_id: solisInverterId || null,
      solis_api_base: solisApiBase || null,
      meteronline_username: moUser || null,
      meteronline_password: moPass || null,
      import_meter_number: importMeter || null,
      export_meter_number: exportMeter || null,
      generation_meter_number: genMeter || null,
      // Legacy fields — only send if the user filled them in
      meteronline_api_url: moUrl || null,
      meteronline_api_key: moKey || null,
      latitude: latitude === "" ? null : Number(latitude),
      longitude: longitude === "" ? null : Number(longitude),
      performance_ratio: pr === "" || pr == null ? null : Number(pr),
      pnl_ppa_p_per_kwh: numOrNull(ppaRate),
      pnl_export_p_per_kwh: numOrNull(exportRate),
      pnl_grid_p_per_kwh: numOrNull(gridRate),
      pnl_admin_p_per_kwh: numOrNull(adminRate),
      opex_per_kwp_gbp: numOrNull(opexKwp),
    };
    try {
      if (isEdit) {
        await api.patch(`/projects/${site.id}`, body);
        // PATCH filters null so an explicit clear needs the dedicated endpoint.
        // Only fire clear for fields the user actually blanked (was set → is now "").
        const cleared = [];
        const wasSet = (v) => v !== null && v !== undefined && v !== "";
        const nowBlank = (v) => v === "" || v === null || v === undefined;
        if (wasSet(site.pnl_ppa_p_per_kwh) && nowBlank(ppaRate)) cleared.push("pnl_ppa_p_per_kwh");
        if (wasSet(site.pnl_export_p_per_kwh) && nowBlank(exportRate)) cleared.push("pnl_export_p_per_kwh");
        if (wasSet(site.pnl_grid_p_per_kwh) && nowBlank(gridRate)) cleared.push("pnl_grid_p_per_kwh");
        if (wasSet(site.pnl_admin_p_per_kwh) && nowBlank(adminRate)) cleared.push("pnl_admin_p_per_kwh");
        if (wasSet(site.opex_per_kwp_gbp) && nowBlank(opexKwp)) cleared.push("opex_per_kwp_gbp");
        if (cleared.length) {
          await api.post(`/projects/${site.id}/analytics/clear-rate-overrides`, { fields: cleared });
        }
      } else {
        await api.post("/projects", body);
      }
      toast.success(isEdit ? "Site updated" : "Site created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/45 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="add-site-dialog">
      <div className="bg-white w-full max-w-2xl border border-[var(--line)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="label">{isEdit ? "Edit site" : "New site"}</div>
            <div className="font-display text-xl font-semibold mt-1">
              {isEdit ? name : "Add a solar site"}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100" data-testid="close-dialog-btn">
            <X size={16} />
          </button>
        </div>

        <div className="flex border-b border-[var(--line)] bg-[var(--surface-2)] overflow-x-auto">
          {[
            { key: "basics", label: "Basics" },
            { key: "solaredge", label: "SolarEdge" },
            { key: "solis", label: "Solis" },
            { key: "meteronline", label: "MeterOnline" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`dialog-tab-${t.key}`}
                    className={`px-4 py-3 text-xs font-mono uppercase tracking-widest ${tab === t.key ? "bg-white border-b-2 border-[var(--accent)] -mb-px" : "muted"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-4">
          {tab === "basics" && (
            <>
              <div>
                <div className="label mb-1.5">Data source</div>
                <div className="flex gap-1 bg-[var(--surface-2)] border border-[var(--line)]" data-testid="data-source-toggle">
                  {[
                    { k: "solaredge", label: "SolarEdge" },
                    { k: "meteronline", label: "MeterOnline / HH" },
                  ].map(o => (
                    <button key={o.k} onClick={() => setDataSource(o.k)}
                            data-testid={`data-source-${o.k}`}
                            className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest ${dataSource === o.k ? "bg-[var(--ink)] text-white" : "muted"}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] muted mt-1.5">
                  Solar production for this site is fetched from the selected platform once per day (03:00 UTC), with automatic 7-day gap detection and backfill.
                </div>
              </div>
              <Row label="Site name">
                <input className="input-swiss" value={name} onChange={(e) => setName(e.target.value)}
                       data-testid="site-name-input" placeholder="Indurent Rugby DC1" />
              </Row>
              <Row label="Customer">
                <input className="input-swiss" value={customer} onChange={(e) => setCustomer(e.target.value)}
                       data-testid="site-customer-input" placeholder="Indurent Ltd" />
              </Row>
              <Row label="Address">
                <input className="input-swiss" value={address} onChange={(e) => setAddress(e.target.value)}
                       data-testid="site-address-input" placeholder="123 Warehouse Rd" />
              </Row>
              <Row label="Postcode">
                <input className="input-swiss" value={postcode} onChange={(e) => setPostcode(e.target.value)}
                       data-testid="site-postcode-input" placeholder="CV21 3AA" />
              </Row>
              <div className="text-[11px] muted flex items-center gap-1.5" data-testid="geo-auto-note">
                <MapPin size={11} /> Latitude &amp; longitude are looked up automatically in the background
                from the postcode / address after saving — no manual entry needed. Set the Performance
                Ratio on the project&apos;s Design tab.
              </div>
            </>
          )}

          {tab === "solaredge" && (
            <>
              <div className="bg-[var(--surface-2)] p-4 border border-[var(--line)]">
                <div className="label mb-2 flex items-center gap-2"><Link2 size={11} /> Quick import</div>
                <div className="text-xs muted mb-3">
                  Paste a SolarEdge monitoring URL, or `siteId,apiKey` pair — I&apos;ll parse it.
                </div>
                <div className="flex gap-2">
                  <input className="input-swiss flex-1" value={apiString}
                         onChange={(e) => setApiString(e.target.value)}
                         placeholder="https://monitoringapi.solaredge.com/site/12345/…?api_key=ABC…"
                         data-testid="se-api-string-input" />
                  <button onClick={parseString} className="btn-ghost" data-testid="se-parse-btn">Parse</button>
                </div>
              </div>
              <Row label="Site ID">
                <input className="input-swiss" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                       data-testid="se-site-id-input" placeholder="12345" />
              </Row>
              <Row label="API Key">
                <input className="input-swiss font-mono" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                       data-testid="se-api-key-input" placeholder="ABC1234567890XYZWERT12" />
              </Row>
              <div className="text-[11px] muted">
                The 15-min sync loop hits SolarEdge once per hour per site. On the free tier
                (~300 calls/day/key) you can safely run up to ~12 sites per key.
              </div>
            </>
          )}

          {tab === "solis" && (
            <>
              <div className="bg-[var(--surface-2)] p-4 border border-[var(--line)]">
                <div className="label mb-2 flex items-center gap-2"><Link2 size={11} /> Solis (SolisCloud) connection</div>
                <div className="text-xs muted mb-1">
                  Generation-only source for Ginlong Solis inverters. HMAC-SHA1 auth against SolisCloud API v2.
                </div>
                <div className="text-[11px] muted">
                  Get Key ID + Key Secret from SolisCloud → Service → API Management (may require support ticket first).
                  Solis is treated as a <strong>drop-in for SolarEdge</strong> — the sync layer will use it if SolarEdge isn't configured.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Row label="Key ID">
                  <input className="input-swiss font-mono text-xs" value={solisKeyId}
                         onChange={(e) => setSolisKeyId(e.target.value)}
                         placeholder="Visitor ID from SolisCloud"
                         autoComplete="off"
                         data-testid="solis-key-id-input" />
                </Row>
                <Row label="Key Secret">
                  <input className="input-swiss font-mono text-xs" type="password" value={solisKeySecret}
                         onChange={(e) => setSolisKeySecret(e.target.value)}
                         placeholder="private signing key"
                         autoComplete="new-password"
                         data-testid="solis-key-secret-input" />
                </Row>
              </div>
              <Row label="Inverter ID">
                <div className="flex gap-1">
                  <input className="input-swiss font-mono text-xs flex-1" value={solisInverterId}
                         onChange={(e) => setSolisInverterId(e.target.value)}
                         placeholder="from /v1/api/inverterList or your Solis dashboard"
                         data-testid="solis-inverter-id-input" />
                  <button type="button" onClick={testSolis}
                          disabled={solisTest?.loading}
                          className="btn-ghost text-[10px] disabled:opacity-50"
                          data-testid="solis-test-btn">Test</button>
                </div>
              </Row>
              <Row label="API base URL (optional)">
                <input className="input-swiss font-mono text-xs" value={solisApiBase}
                       onChange={(e) => setSolisApiBase(e.target.value)}
                       placeholder="https://www.soliscloud.com:13333 (default)"
                       data-testid="solis-base-input" />
              </Row>
              {solisTest && !solisTest.loading && (
                <div className={`p-3 text-xs border ${solisTest.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}
                     data-testid="solis-test-result">
                  {solisTest.ok
                    ? <>✓ Auth OK · {solisTest.station_count} station(s) visible on this account.</>
                    : <>✗ {solisTest.error}</>}
                </div>
              )}
            </>
          )}


          {tab === "meteronline" && (
            <>
              <div className="bg-[var(--surface-2)] p-4 border border-[var(--line)]">
                <div className="label mb-2 flex items-center gap-2"><Link2 size={11} /> MeterOnline connection</div>
                <div className="text-xs muted mb-1">
                  Half-hourly kWh from MeterOnline (metermanager.co.uk) — synced daily at 03:00 UTC with a 7-day rolling gap-check.
                </div>
                <div className="text-[11px] muted">
                  Auth: HTTP Basic. Set your <strong>API password</strong> in MeterOnline via Account Management → Update Api Password.
                  Set <code>data source → MeterOnline / HH</code> on the Basics tab to activate the sync.
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Row label="Username">
                  <input className="input-swiss" value={moUser}
                         onChange={(e) => setMoUser(e.target.value)}
                         placeholder="your MeterOnline login"
                         autoComplete="off"
                         data-testid="mo-username-input" />
                </Row>
                <Row label="API Password">
                  <input className="input-swiss font-mono" type="password" value={moPass}
                         onChange={(e) => setMoPass(e.target.value)}
                         placeholder="from Account → Update Api Password"
                         autoComplete="new-password"
                         data-testid="mo-password-input" />
                </Row>
              </div>

              <div className="text-[11px] muted -mt-2">
                Enter <strong>meter numbers</strong> (serial numbers) — same identifier MeterOnline shows for each meter in your account.
                If your <em>import and export are on one bi-directional meter</em>, use the same number for both fields; we'll fetch export via the <code>/reverse-…</code> endpoint automatically.
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Row label="Import Meter (required)">
                  <div className="flex gap-1">
                    <input className="input-swiss font-mono text-xs flex-1" value={importMeter}
                           onChange={(e) => setImportMeter(e.target.value)}
                           data-testid="mo-import-meter-input" placeholder="e.g. 1622160461G" />
                    <button type="button" onClick={() => testMeterOnline(importMeter, false)}
                            disabled={moTest?.loading}
                            className="btn-ghost text-[10px] disabled:opacity-50"
                            data-testid="mo-test-import-btn">Test</button>
                  </div>
                </Row>
                <Row label="Export Meter (required)">
                  <div className="flex gap-1">
                    <input className="input-swiss font-mono text-xs flex-1" value={exportMeter}
                           onChange={(e) => setExportMeter(e.target.value)}
                           data-testid="mo-export-meter-input" placeholder="same as import if bi-directional" />
                    <button type="button" onClick={() => testMeterOnline(
                              exportMeter,
                              importMeter.trim() && exportMeter.trim() === importMeter.trim())}
                            disabled={moTest?.loading}
                            className="btn-ghost text-[10px] disabled:opacity-50"
                            data-testid="mo-test-export-btn">Test</button>
                  </div>
                </Row>
                <Row label="Generation Meter (optional)">
                  <div className="flex gap-1">
                    <input className="input-swiss font-mono text-xs flex-1" value={genMeter}
                           onChange={(e) => setGenMeter(e.target.value)}
                           data-testid="mo-gen-meter-input" placeholder="blank = use SolarEdge" />
                    <button type="button" onClick={() => testMeterOnline(genMeter, false)}
                            disabled={moTest?.loading}
                            className="btn-ghost text-[10px] disabled:opacity-50"
                            data-testid="mo-test-gen-btn">Test</button>
                  </div>
                </Row>
              </div>
              {importMeter && exportMeter && importMeter.trim() === exportMeter.trim() && (
                <div className="text-[11px] text-[var(--accent)] font-mono">
                  ⓘ Bi-directional single meter detected — export will be fetched via the reverse-HH endpoint.
                </div>
              )}

              <div className="mt-2">
                <button type="button" onClick={() => setShowLegacy(v => !v)}
                        className="text-[11px] font-mono uppercase tracking-widest muted hover:text-[var(--ink)]"
                        data-testid="mo-toggle-legacy-btn">
                  {showLegacy ? "▾" : "▸"} Legacy generic-vendor (Bearer key) fallback
                </button>
                {showLegacy && (
                  <div className="mt-2 space-y-3 p-3 border border-[var(--line)] bg-[var(--surface-2)]">
                    <div className="text-[11px] muted">
                      Only used when Username + API Password are blank. Kept for Stark / TMA / Elexon-portal integrations that share the HH schema.
                    </div>
                    <Row label="API URL (supports {mpan} placeholder)">
                      <input className="input-swiss font-mono text-xs" value={moUrl}
                             onChange={(e) => setMoUrl(e.target.value)}
                             placeholder="https://api.example.com/mpans/{mpan}/readings"
                             data-testid="mo-url-input" />
                    </Row>
                    <Row label="API Key (Bearer)">
                      <input className="input-swiss font-mono" value={moKey}
                             onChange={(e) => setMoKey(e.target.value)}
                             data-testid="mo-key-input" placeholder="Bearer token" />
                    </Row>
                  </div>
                )}
              </div>

              {moTest && !moTest.loading && (
                <div className={`p-3 text-xs border ${moTest.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}
                     data-testid="mo-test-result">
                  {moTest.ok ? (
                    <>✓ {moTest.sample_count} readings pulled · {moTest.total_kwh_24h} kWh over last 24 h.
                       {moTest.sample && moTest.sample[0] && (
                         <span className="font-mono muted"> Sample: {moTest.sample[0].ts} → {moTest.sample[0].kwh} kWh</span>
                       )}
                    </>
                  ) : (
                    <>✗ {moTest.error}</>
                  )}
                </div>
              )}
              <div className="text-[11px] muted">
                <strong>Self-consumption logic:</strong> when a Generation MPAN is set, we compute
                <code> self-consumption = generation − export</code> per 15-min bucket.
                Without it, we treat <code>actual = export</code> and self-consumption stays null
                (understates real production by the on-site consumption share).
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-[var(--line)] bg-[var(--surface-2)]">
          <button onClick={onClose} className="btn-ghost" data-testid="cancel-btn">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-site-btn">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Create site")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="label mb-1.5">{label}</div>
      {children}
    </div>
  );
}
